// Map 2.0 layer control: one compact pill that opens a grouped bottom sheet with
// a marker legend, plus a small "Nearby parking" pill. Live community spots stay
// dominant and everything beyond live + free + paid parking is OFF by default.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Layers, X, Check, Navigation, ChevronRight } from "lucide-react";
import { LAYER_GROUPS, activeSecondaryCount, LIVE_SPOT_TYPES } from "../lib/mapLayers";
import { placeIconHtml } from "../lib/placeIcons";
import { useBackdropDismiss } from "../lib/useBackdropDismiss";
import { track } from "../lib/analytics";

function Toggle({ on, disabled }) {
  return (
    <span
      className={`w-9 h-5 rounded-full relative transition-colors shrink-0 ${
        disabled ? "bg-white/5" : on ? "bg-[#ccff00]" : "bg-white/10"
      }`}
    >
      <span
        className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
          on ? "left-[18px] bg-[#0a0a0c]" : "left-0.5 bg-zinc-400"
        }`}
      />
    </span>
  );
}

function LayerGlyph({ itemKey }) {
  if (itemKey === "community_pending") {
    return (
      <span
        className="w-6 flex justify-center shrink-0 scale-90"
        dangerouslySetInnerHTML={{ __html: placeIconHtml("needs_check") }}
      />
    );
  }
  if (itemKey === "live") {
    return (
      <span className="w-6 flex justify-center shrink-0">
        <span className="w-4 h-4 rounded-full bg-[#ccff00] ring-2 ring-[#ccff00]/25" />
      </span>
    );
  }
  if (itemKey === "private") {
    return (
      <span className="w-6 flex justify-center shrink-0">
        <span className="w-4 h-4 rounded-md bg-[#111827] border-[1.5px] border-[#ccff00] text-[#ccff00] text-[9px] font-extrabold flex items-center justify-center">
          €
        </span>
      </span>
    );
  }
  const html = placeIconHtml(itemKey);
  if (["restaurants", "cafes", "barbers", "parking_chance"].includes(itemKey)) {
    return (
      <span className="w-6 flex justify-center shrink-0">
        <span className="w-3.5 h-3.5 rounded bg-white/15" />
      </span>
    );
  }
  return (
    <span
      className="w-6 flex justify-center shrink-0 scale-90"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default function MapLayersControl({ layers, onChange, onOpenNearby }) {
  const [open, setOpen] = React.useState(false);
  const dismissProps = useBackdropDismiss(open, () => setOpen(false));
  const count = activeSecondaryCount(layers);

  const toggle = (key) => {
    const next = { ...layers, [key]: !layers[key] };
    onChange(next);
    track(next[key] ? "map_layer_activated" : "map_layer_deactivated", { layer: key });
  };

  return (
    <>
      <div className="absolute left-2 top-14 sm:left-3 sm:top-16 z-[1000] flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            track("layers_sheet_opened");
          }}
          className="h-8 pl-2.5 pr-3 inline-flex items-center gap-1.5 rounded-full bg-[#0a0a0c]/75 border border-white/15 backdrop-blur-md text-[11px] font-medium text-zinc-100 active:scale-95 transition-transform"
          data-testid="map-layers-btn"
          aria-label="Map layers and filters"
        >
          <Layers className="w-3.5 h-3.5 text-[#ccff00]" />
          Layers
          {count > 0 && (
            <span
              className="ml-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#ccff00] text-[#0a0a0c] text-[10px] font-bold inline-flex items-center justify-center"
              data-testid="map-layers-count"
            >
              {count}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => {
            track("find_parking_nearby_clicked", { from: "pill" });
            onOpenNearby?.();
          }}
          className="h-8 w-8 inline-flex items-center justify-center rounded-full bg-[#0a0a0c]/75 border border-white/15 backdrop-blur-md active:scale-95 transition-transform"
          data-testid="nearby-parking-btn"
          aria-label="Nearby parking options"
        >
          <Navigation className="w-3.5 h-3.5 text-[#ccff00]" />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[2000] bg-black/60"
              {...dismissProps}
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 280 }}
              className="fixed inset-x-0 bottom-0 z-[2001] max-h-[80vh] overflow-y-auto bg-[#141416] border-t border-white/10 rounded-t-3xl p-4 pb-7"
              data-testid="map-layers-sheet"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
                    Map layers
                  </div>
                  <div className="font-heading text-lg font-semibold text-white mt-0.5">
                    Choose what you see
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"
                  data-testid="map-layers-close"
                  aria-label="Close map layers"
                >
                  <X className="w-4 h-4 text-zinc-400" />
                </button>
              </div>

              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  track("find_parking_nearby_clicked", { from: "layers_sheet" });
                  onOpenNearby?.();
                }}
                className="w-full mb-4 p-3 rounded-xl bg-[#ccff00]/[0.07] border border-[#ccff00]/25 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
                data-testid="open-nearby-parking"
              >
                <span className="w-9 h-9 rounded-lg bg-[#ccff00] flex items-center justify-center shrink-0">
                  <Navigation className="w-4 h-4 text-[#0a0a0c]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold text-white">Nearby parking</span>
                  <span className="block text-[11px] text-zinc-400">
                    Best options around you — free or paid
                  </span>
                </span>
                <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />
              </button>

              <div className="space-y-4">
                {LAYER_GROUPS.map((g) => (
                  <div key={g.key} data-testid={`layer-group-${g.key}`}>
                    <div className="flex items-center gap-2">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                        {g.label}
                      </div>
                      {g.soon && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-zinc-500 border border-white/10">
                          {g.soon}
                        </span>
                      )}
                    </div>
                    {g.hint && <p className="mt-1 text-[11px] text-zinc-600 leading-snug">{g.hint}</p>}
                    <div className="mt-2 rounded-xl border border-white/10 divide-y divide-white/5 overflow-hidden">
                      {g.items.map((it) => {
                        const on = !!layers[it.key];
                        return (
                          <button
                            key={it.key}
                            type="button"
                            disabled={it.disabled}
                            onClick={() => !it.disabled && toggle(it.key)}
                            className={`w-full px-3 py-2.5 flex items-center gap-3 text-left ${
                              it.disabled ? "opacity-45" : "active:bg-white/5"
                            }`}
                            data-testid={`layer-toggle-${it.key}`}
                            aria-pressed={on}
                          >
                            <LayerGlyph itemKey={it.key} />
                            <span className="flex-1 min-w-0">
                              <span className="block text-[13px] text-white leading-tight">{it.label}</span>
                              {it.hint && (
                                <span className="block text-[11px] text-zinc-500 leading-tight mt-0.5">
                                  {it.hint}
                                </span>
                              )}
                            </span>
                            <Toggle on={on} disabled={it.disabled} />
                          </button>
                        );
                      })}
                      {g.key === "parking" && layers.live && (
                        <div className="px-3 py-2 flex flex-wrap gap-x-3 gap-y-1" data-testid="live-spot-legend">
                          {LIVE_SPOT_TYPES.map((t) => (
                            <span key={t.key} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500">
                              <span className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                              {t.label}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 text-[11px] text-zinc-600 leading-relaxed">
                Parking information comes from OpenStreetMap and from locations verified by SpotDrop.
                Only live SpotDrops are confirmed by a driver — always check the signs on site.
              </div>

              <button
                type="button"
                onClick={() => setOpen(false)}
                className="mt-4 w-full h-11 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold inline-flex items-center justify-center gap-2"
                data-testid="map-layers-done"
              >
                <Check className="w-4 h-4" /> Done
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
